<template>
	<div>
		<!-- Expose dropdown time to QA -->
		<div
			class="d-none"
			test-id="dropdown-time"
			:data-minutes="minutes"
			:data-seconds="seconds"
		></div>

		<!-- Video container -->
		<div
			class="video-container"
			:class="{ vertical: isVertical }"
			@click="handleContainerClick"
		>
			<!-- JW Video (replaced by JW api) -->
			<div :id="videoHash" ref="video"></div>

			<!-- Custom video overlay -->
			<div
				v-if="overlayVisible && !setup.controls"
				ref="overlay"
				class="overlay"
			>
				<span class="overlay__text fade-in">Click to play</span>
			</div>
		</div>
	</div>
</template>

<script>
import safeSessionStorage from '@js/safe-session-storage';

export default {
	props: {
		id: String,
		minutes: Number,
		seconds: Number,
		setup: {
			type: Object,
			default: {
				aspectratio: '9:16',
				controls: false,
			},
		},
		vertical: {
			default: false,
			type: [Boolean, String],
		},
		verticalBreakpoint: {
			type: Number,
			default: 1000,
		},
		verticalId: String,
	},

	data() {
		return {
			overlayVisible: false,
			video: null,
			videoHash: Math.floor(
				new Date().getTime() * Math.random()
			).toString(),
			videoPlaying: false,
			sessionKey: null,
			isVertical: false,
			isDropdownEventEmitted: false,
		};
	},

	computed: {
		shouldEnableVertical() {
			return this.vertical === true || this.vertical === 'true';
		},
	},

	mounted() {
		if (document.readyState === 'complete') {
			this.setupPlayer();
		} else {
			const readyStateCompleteListener = () => {
				if (document.readyState === 'complete') {
					this.setupPlayer();
					document.removeEventListener(
						'readystatechange',
						readyStateCompleteListener
					);
				}
			};

			document.addEventListener(
				'readystatechange',
				readyStateCompleteListener
			);
		}
	},

	destroyed() {
		window.removeEventListener('resize', this.handleResize);
	},

	methods: {
		handleResize() {
			if (
				this.shouldEnableVertical &&
				window.innerWidth <= this.verticalBreakpoint
			) {
				this.video.setConfig({ aspectratio: this.setup.aspectratio });
				this.isVertical = true;
			} else {
				this.video.setConfig({ aspectratio: '16:9' });
				this.isVertical = false;
			}
		},

		handleContainerClick(event) {
			if (this.setup.controls) return;

			this.overlayVisible = false;

			if (this.videoPlaying) {
				this.video.pause();
			} else {
				this.video.play();

				// const isMobile =
				// 	!!navigator.userAgent &&
				// 	/iPad|iPhone|iPod|Android|Opera Mini|Opera Touch|Opera Mobile|BlackBerry|Windows Phone|webOS|IEMobile/.test(
				// 		navigator.userAgent
				// 	);
				// if (isMobile) {
				// 	this.video.setFullscreen(true);
				// }
			}

			this.videoPlaying = !this.videoPlaying;
		},

		handleFullscreen(event) {
			event.stopPropagation();
			this.video.setFullscreen();
		},

		setupPlayer() {
			const hasVideoBeenWatched =
				JSON.parse(
					safeSessionStorage.getItem(`${this.id}-jw-watched`)
				) || false;

			if (hasVideoBeenWatched) {
				this.showBanners();
			}

			const isVertical =
				this.vertical && window.innerWidth <= this.verticalBreakpoint;
			const videoId =
				isVertical && this.verticalId ? this.verticalId : this.id;
			this.setup.playlist = `https://cdn.jwplayer.com/v2/media/${videoId}?format=json`;

			this.video = jwplayer(this.videoHash);
			this.video.setup(this.setup);

			this.video.on('time', this.timeHandler);

			this.video.on('complete', this.handleDropdown);

			this.video.on('ready', () => {
				const lastTime =
					JSON.parse(
						safeSessionStorage.getItem(`${this.id}-jw-time`)
					) || 0;

				if (lastTime > 0) {
					this.video.seek(lastTime);
					this.video.pause();
				}

				if (!this.setup.controls) {
					const fullscreenButton = document.createElement('div');

					fullscreenButton.innerHTML =
						'<svg fill="#FFF" xmlns="http://www.w3.org/2000/svg" height="1em" viewBox="0 0 448 512"><path d="M32 32C14.3 32 0 46.3 0 64v96c0 17.7 14.3 32 32 32s32-14.3 32-32V96h64c17.7 0 32-14.3 32-32s-14.3-32-32-32H32zM64 352c0-17.7-14.3-32-32-32s-32 14.3-32 32v96c0 17.7 14.3 32 32 32h96c17.7 0 32-14.3 32-32s-14.3-32-32-32H64V352zM320 32c-17.7 0-32 14.3-32 32s14.3 32 32 32h64v64c0 17.7 14.3 32 32 32s32-14.3 32-32V64c0-17.7-14.3-32-32-32H320zM448 352c0-17.7-14.3-32-32-32s-32 14.3-32 32v64H320c-17.7 0-32 14.3-32 32s14.3 32 32 32h96c17.7 0 32-14.3 32-32V352z"/></svg>';

					fullscreenButton.className =
						'fullscreen-icon overlay__fullscreen';
					fullscreenButton.addEventListener(
						'click',
						this.handleFullscreen
					);

					const videoElement = document.getElementById(
						this.videoHash
					);
					videoElement.appendChild(fullscreenButton);
				}

				this.overlayVisible = true;
				this.$nextTick(this.handleResize);
				this.hideSkeleton();

				window.addEventListener('resize', this.handleResize);
			});

			this.video.on('pause', () => {
				this.overlayVisible = true;
				this.videoPlaying = false;
			});

			this.video.on('setupError', this.errorHandler);
			this.video.on('error', this.errorHandler);
		},

		errorHandler(event = { code: 'setupError' }) {
			this.hideSkeleton();

			const trackPageReloadsSessionKey = `${this.id}-jw-reloaded-${event.code}`;
			let countOfPageReloads =
				safeSessionStorage.getItem(trackPageReloadsSessionKey) || 0;

			if (countOfPageReloads < 5) {
				safeSessionStorage.setItem(
					trackPageReloadsSessionKey,
					++countOfPageReloads
				);
				setTimeout(() => location.reload(), countOfPageReloads * 1500);
			}
		},

		timeHandler(event) {
			const { currentTime } = event;

			safeSessionStorage.setItem(`${this.id}-jw-time`, currentTime);

			const timeInSeconds = this.minutes * 60 + this.seconds;
			if (currentTime > timeInSeconds) {
				this.handleDropdown();
			}
		},

		handleDropdown() {
			this.showBanners();
			safeSessionStorage.setItem(
				`${this.id}-jw-watched`,
				JSON.stringify(true)
			);

			if (!this.isDropdownEventEmitted) {
				// Emit Event
				let emit_data = {
					event: 'DropdownTriggered',
					video_id: this.id,
					time: this.seconds + this.minutes * 60,
				};
				window.dataLayer.push(emit_data);
				this.isDropdownEventEmitted = true;
			}
		},

		hideSkeleton() {
			const skeleton = document.querySelector(`.jw-skeleton-${this.id}`);
			if (skeleton) skeleton.className = '';

			const spinner = document.querySelector(`.jw-spinner-${this.id}`);
			if (spinner) spinner.parentNode.removeChild(spinner);
		},

		showBanners() {
			const banners = document.querySelectorAll('.after-banner');

			for (let i = 0; i < banners.length; ++i) {
				banners[i].style.display = 'block';
			}
		},
	},
};
</script>

<style lang="scss">
.jw-video-thumbnail,
.jw-video-thumbnail-container {
	width: 100%;
	height: 100%;
}

.overlay__fullscreen {
	background-color: rgb(112, 186, 247);
	color: white;
	width: 44px;
	height: 44px;
	position: absolute;
	bottom: 0;
	right: 0;
	font-size: 24px;
	display: flex;
	align-items: center;
	justify-content: center;
	border-top-left-radius: 5px;
	z-index: 10;
}

.overlay__fullscreen:hover {
	background-color: rgba(112, 186, 247, 0.8);
	cursor: pointer;
}

.vertical .jw-video-thumbnail-generated {
	display: none;
}
</style>

<style lang="scss" scoped>
.video-container {
	position: relative;
	margin: auto;
}

.overlay {
	position: absolute;
	top: 0;
	width: 100%;
	height: 100%;
	display: flex;
	align-items: center;
	justify-content: center;
	z-index: 11;
	background-color: rgba(0, 0, 0, 0.3);
	pointer-events: none;
	text-align: center;
}

.overlay__text {
	color: white;
	text-transform: uppercase;
	font-weight: bold;
	font-style: italic;
	font-size: 10vw;
	line-height: 100%;
	@media screen and (min-width: 768px) {
		font-size: 4vw;
	}
}
</style>
